import React, { useState, useEffect } from "react";
import "../assets/css/modal.css";
import "../utils/Base_Url.js";
import { Base_Url } from "../utils/Base_Url.js";
import axios from "axios";
import SuccessModal from "../component/successModal.js";
import ErrorModal from "./errorModal";

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB, matches the limit stated in the UI

// Allowed types keyed by extension, each with the magic-number signature the
// real file bytes must start with, so an attacker can't smuggle in an .html/.exe
// by simply renaming it or spoofing the MIME type.
const ALLOWED_FILE_TYPES = {
  jpg: { mime: "image/jpeg", signature: [0xff, 0xd8, 0xff] },
  jpeg: { mime: "image/jpeg", signature: [0xff, 0xd8, 0xff] },
  png: { mime: "image/png", signature: [0x89, 0x50, 0x4e, 0x47] },
  pdf: { mime: "application/pdf", signature: [0x25, 0x50, 0x44, 0x46] },
  doc: { mime: "application/msword", signature: [0xd0, 0xcf, 0x11, 0xe0] },
  docx: {
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    signature: [0x50, 0x4b, 0x03, 0x04],
  },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+()\-\s]{7,20}$/;
const MAX_REPORT_LENGTH = 5000;

const readFileSignature = (file, length) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, length));
  });

const validateFile = async (file) => {
  const extension = file.name.split(".").pop().toLowerCase();
  const expected = ALLOWED_FILE_TYPES[extension];

  if (!expected) {
    return "Unsupported file type. Allowed formats: JPEG, PNG, DOC, DOCX, PDF.";
  }
  if (file.type && file.type !== expected.mime) {
    return "The file's content type does not match its extension.";
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "File is too large. Maximum file size is 2MB.";
  }

  const signature = await readFileSignature(file, expected.signature.length);
  const matchesSignature = expected.signature.every(
    (byte, index) => signature[index] === byte,
  );
  if (!matchesSignature) {
    return "The file's content does not match its extension.";
  }

  return null;
};

const Modal = ({ isOpen, onClose }) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [textValue, setTextValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(" ");
  const [agreed, setAgreed] = useState(false);

  const handleCheckboxChange = () => {
    setAgreed(!agreed);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0]; // Use the first selected file, which will be a single file
    e.target.value = "";
    if (!file) {
      return;
    }

    const validationError = await validateFile(file);
    if (validationError) {
      setFileError(validationError);
      setSelectedFile(null);
      return;
    }

    setFileError("");
    setSelectedFile(file);
  };
  const closeSuccessModal = () => {
    setSuccess(false);
    onClose();
  };

  const closeErrorModal = () => {
    setError(" ");
  };

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
    if (e.target.value === "No") {
      setShowConfirmation(true);
    } else {
      setShowConfirmation(false);
    }
  };

  const handleTextareaChange = (e) => {
    setTextValue(e.target.value);
  };

  const handleSubmit = async () => {
    if (!textValue.trim()) {
      setError("Please provide details of the report.");
      return;
    }
    if (textValue.length > MAX_REPORT_LENGTH) {
      setError(`Report details must be under ${MAX_REPORT_LENGTH} characters.`);
      return;
    }
    if (selectedOption === "No") {
      if (!fullName.trim()) {
        setError("Please provide your full name.");
        return;
      }
      if (!EMAIL_REGEX.test(email.trim())) {
        setError("Please provide a valid email address.");
        return;
      }
      if (phoneNumber.trim() && !PHONE_REGEX.test(phoneNumber.trim())) {
        setError("Please provide a valid phone number.");
        return;
      }
    }

    setLoading(true);
    const apiUrl = `${Base_Url}/api/reportIncident`;

    const formData = new FormData();
    formData.append("Description", textValue.trim());
    formData.append("Organisation", organization.trim() ? organization.trim() : "General");
    formData.append("IncidentType", "Whistleblowing Report");
    formData.append("how", "Web Portal");
    formData.append("source", "1");

    if (selectedFile) {
      formData.append("file", selectedFile);
    }

    if (selectedOption === "No") {
      // Include additional fields for 'No' selection
      formData.append("ReporterFullName", fullName.trim());
      formData.append("ReporterEmail", email.trim());
      formData.append("ReportAnonymous", selectedOption);
      formData.append("ReporterPhoneNumber", phoneNumber.trim());
      formData.append("ReporterOrgName", organization.trim());
    } else {
      formData.append("ReporterFullName", "");
      formData.append("ReporterEmail", "");
      formData.append("ReportAnonymous", selectedOption);
      formData.append("ReporterPhoneNumber", "");
      formData.append("ReporterOrgName", "");
    }

    // Make a POST request to the API endpoint using Axios
    try {
      const response = await axios.post(apiUrl, formData, {
        headers: {
          "APIKey": "4d53bKce03ec34cP0a91118M2d4c228eGe6c",
        },
      });

      if (response.status === 200) {
        setLoading(false);
        setSuccess(true);
        console.log("Request sent successfully.");
      } else {
        console.log("Request failed.");
      }
    } catch (error) {
      console.log(error.response?.data?.message || "Request failed.");
      setError(error.response?.data?.message || "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      console.log("useEffect triggered");
      setTextValue("");
      setEmail("");
      setFullName("");
      setPhoneNumber("");
      setSelectedFile(null);
      setFileError("");
      setSelectedOption("");
    }
  }, [success]);

  return (
    <div className={`modal ${isOpen ? "open" : ""}`}>
      {loading ? (
        <div className="loading-spinner"></div>
      ) : success ? (
        <SuccessModal
          successModalOpen={success}
          closeSuccessModal={closeSuccessModal}
        />
      ) : error !== " " ? (
        <ErrorModal closeErrorModal={closeErrorModal} error={error} />
      ) : (
        <div className="modal-content">
          <h2 className="modal-heading">Make a WhistleBlowing Report</h2>
          <p className="first-text">
            Do you want to submit a report anonymously?
          </p>

          <select
            className="selection"
            value={selectedOption}
            onChange={handleSelectChange}
            style={{ width: "100%" }}
          >
            <option className="first-text">Choose an option</option>
            <option value="Yes">Yes </option>
            <option value="No">No </option>
          </select>
          {showConfirmation && (
            <div className="confirmation-text">
              <p className="first-text">Full Name</p>
              <input
                className="confirmation-textarea"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
              />{" "}
              <p className="first-text">Email Address</p>
              <input
                className="confirmation-textarea"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />{" "}
              <p className="first-text">
                Phone Number <span className="span">(optional)</span>
              </p>
              <input
                className="confirmation-textarea"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
              />{" "}
              <p className="first-text">
                Organization <span className="span">(optional)</span>
              </p>
              <input
                className="confirmation-textarea"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Enter your organization name"
              />
            </div>
          )}

          <div className="no-select">
            <p className="first-text">Provide details of the report</p>
            <textarea
              className="details-textarea"
              value={textValue}
              onChange={handleTextareaChange}
              placeholder="Enter details here"
            />
            <p className="first-text">
              Upload Supporting Document(s)
              <span className="span"> (optional)</span>
            </p>

            <div className="file-input-container">
              <p className="first-text">You can only select one file</p>

              <label className="file-label">
                Browse
                <input
                  type="file"
                  accept=".jpg, .jpeg, .png, .pdf, .doc, .docx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {fileError && <p className="text-red">{fileError}</p>}

            <div className={selectedFile ? "file-title" : "empty-file-title"}>
              <span>{selectedFile ? selectedFile.name : ""}</span>
              {selectedFile && (
                <button
                  className="close-btn"
                  onClick={() => {
                    setSelectedFile(null);
                    setFileError("");
                  }}
                >
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M8.94002 7.9998L11.8067 5.1398C11.9322 5.01426 12.0027 4.844 12.0027 4.66646C12.0027 4.48893 11.9322 4.31867 11.8067 4.19313C11.6811 4.0676 11.5109 3.99707 11.3334 3.99707C11.1558 3.99707 10.9856 4.0676 10.86 4.19313L8.00002 7.0598L5.14002 4.19313C5.01448 4.0676 4.84422 3.99707 4.66669 3.99707C4.48915 3.99707 4.31889 4.0676 4.19335 4.19313C4.06782 4.31867 3.99729 4.48893 3.99729 4.66646C3.99729 4.844 4.06782 5.01426 4.19335 5.1398L7.06002 7.9998L4.19335 10.8598C4.13087 10.9218 4.08127 10.9955 4.04743 11.0767C4.01358 11.158 3.99615 11.2451 3.99615 11.3331C3.99615 11.4211 4.01358 11.5083 4.04743 11.5895C4.08127 11.6708 4.13087 11.7445 4.19335 11.8065C4.25533 11.8689 4.32906 11.9185 4.4103 11.9524C4.49154 11.9862 4.57868 12.0037 4.66669 12.0037C4.75469 12.0037 4.84183 11.9862 4.92307 11.9524C5.00431 11.9185 5.07805 11.8689 5.14002 11.8065L8.00002 8.9398L10.86 11.8065C10.922 11.8689 10.9957 11.9185 11.077 11.9524C11.1582 11.9862 11.2453 12.0037 11.3334 12.0037C11.4214 12.0037 11.5085 11.9862 11.5897 11.9524C11.671 11.9185 11.7447 11.8689 11.8067 11.8065C11.8692 11.7445 11.9188 11.6708 11.9526 11.5895C11.9865 11.5083 12.0039 11.4211 12.0039 11.3331C12.0039 11.2451 11.9865 11.158 11.9526 11.0767C11.9188 10.9955 11.8692 10.9218 11.8067 10.8598L8.94002 7.9998Z"
                        fill="#666666"
                      />
                    </svg>
                  </span>
                </button>
              )}
            </div>
            <p className="text-red">Please note the following.</p>
            <ul>
              <li className="text-red">
                If you have chosen to reveal your identity, please ensure that
                all information provided above is accurate
              </li>{" "}
              <li className="text-red">
                If you have not chosen to reveal your identity, please ensure
                that all personal information have been removed from any
                attachment
              </li>
              <li className="text-red">
                The uploaded file must be in JPEG, PNG, Word or PDF format
              </li>
              <li className="text-red">Maximum file size is 2MB</li>
            </ul>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={handleCheckboxChange}
                  className="mr-2"
                />
                <span className="w-[461px] text-black text-sm not-italic font-normal leading-[26px] tracking-[0.28px]">
                  I agree to have my personal data (provided above) to be
                  processed.
                </span>
              </label>
            </div>
            <div className="mt-4 pt-2 mx-auto d-flex hero-button">
              <button className="btn btn-2" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-1"
                onClick={handleSubmit}
                disabled={!agreed || !!fileError}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Modal;
